import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';

const STATUS_LABEL = {
  disconnected: 'Desconectado',
  qrcode: 'Aguardando QR',
  connecting: 'Conectando...',
  connected: 'Conectado',
  banned: 'Banido',
  error: 'Erro',
};

const STATUS_COLOR = {
  disconnected: '#5E7A82',
  qrcode: '#fe8d25',
  connecting: '#fe8d25',
  connected: '#4CB553',
  banned: '#b43737',
  error: '#b43737',
};

function StatusBadge({ status }) {
  const cor = STATUS_COLOR[status] || '#5E7A82';
  return (
    <span
      className="badge"
      style={{
        background: `${cor}22`,
        color: cor,
        border: `1px solid ${cor}55`,
        fontWeight: 600,
        padding: '4px 10px',
      }}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function formatNumero(numero) {
  if (!numero) return '—';
  const limpo = String(numero).replace(/\D/g, '');
  if (limpo.length === 13) {
    return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 9)}-${limpo.slice(9)}`;
  }
  return numero;
}

export default function MartelinhoConexao() {
  const { temPermissao } = useAuth();
  const [canal, setCanal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [qrModal, setQrModal] = useState(null); // { base64, pairing, status, atualizadoEm }
  const pollRef = useRef(null);

  const podeGerenciar = temPermissao('martelinho:gerenciar');

  const carregar = async () => {
    try {
      const res = await axios.get(`${API}/martelinho/conexao`);
      setCanal(res.data?.data || null);
      setErro('');
    } catch (err) {
      setErro('Nao foi possivel carregar a conexao.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!podeGerenciar) {
      setLoading(false);
      return undefined;
    }
    carregar();
    return undefined;
  }, [podeGerenciar]);

  // Polling enquanto pareando OU com modal de QR aberto
  const precisaPolling = useMemo(
    () => qrModal !== null || canal?.status === 'qrcode' || canal?.status === 'connecting',
    [canal, qrModal],
  );

  useEffect(() => {
    if (!precisaPolling) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = setInterval(async () => {
      await carregar();
      if (qrModal) {
        try {
          const res = await axios.get(`${API}/martelinho/conexao/qrcode`);
          setQrModal((prev) => prev && {
            ...prev,
            base64: res.data?.data?.base64,
            pairing: res.data?.data?.pairing_code,
            status: res.data?.data?.status,
            atualizadoEm: res.data?.data?.atualizado_em,
          });
        } catch (err) {
          // QR pode ter expirado/canal desconectado - so o status do canal conta
        }
      }
    }, 3000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [precisaPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fecha modal automaticamente quando parear
  useEffect(() => {
    if (!qrModal) return;
    if (canal?.status === 'connected') {
      setQrModal(null);
      Swal.fire({
        icon: 'success',
        title: 'Numero pareado!',
        text: 'O Martelinho esta conectado.',
        timer: 2000,
        showConfirmButton: false,
      });
    }
  }, [canal, qrModal]);

  const conectar = async () => {
    setSalvando(true);
    try {
      const res = await axios.post(`${API}/martelinho/conexao/connect`);
      const atualizado = res.data?.data;
      setQrModal({
        base64: atualizado?.qrcode_base64 || null,
        pairing: atualizado?.qrcode_pairing_code || null,
        status: atualizado?.status || 'qrcode',
        atualizadoEm: atualizado?.qrcode_atualizado_em,
      });
      try {
        const qrRes = await axios.get(`${API}/martelinho/conexao/qrcode`);
        setQrModal((prev) => prev && {
          ...prev,
          base64: qrRes.data?.data?.base64 || prev.base64,
          pairing: qrRes.data?.data?.pairing_code || prev.pairing,
          status: qrRes.data?.data?.status || prev.status,
          atualizadoEm: qrRes.data?.data?.atualizado_em || prev.atualizadoEm,
        });
      } catch (qrErr) {
        // QR ainda nao gerado - polling vai pegar quando chegar
      }
      await carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro ao conectar', text: err.response?.data?.message || err.message });
    } finally {
      setSalvando(false);
    }
  };

  const desconectar = async () => {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Desconectar o Martelinho?',
      text: 'O bot vai parar de receber e responder mensagens ate parear novamente.',
      showCancelButton: true,
      confirmButtonText: 'Desconectar',
      cancelButtonText: 'Cancelar',
    });
    if (!r.isConfirmed) return;

    setSalvando(true);
    try {
      await axios.post(`${API}/martelinho/conexao/disconnect`);
      await carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro ao desconectar', text: err.response?.data?.message || err.message });
    } finally {
      setSalvando(false);
    }
  };

  if (!podeGerenciar) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">Voce nao tem permissao para gerenciar a conexao do Martelinho.</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="mb-4">
        <h3 className="mb-1" style={{ color: 'var(--portal-text)' }}>Conexao do Martelinho</h3>
        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
          O Martelinho usa uma unica conexao de WhatsApp. Conecte pareando o numero por QR code.
        </p>
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" /></div>
      ) : !canal ? (
        <div className="card p-4 text-center">
          <p className="mb-0 text-muted">Canal do Martelinho nao encontrado. Rode a migration 036_martelinho.sql.</p>
        </div>
      ) : (
        <div className="row">
          <div className="col-12 col-md-6 col-lg-5">
            <div className="card h-100 p-3" style={{ border: '1px solid var(--portal-border)' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-whatsapp" style={{ color: '#25D366', fontSize: '1.4rem' }} />
                <h5 className="mb-0" style={{ color: 'var(--portal-text)' }}>{canal.nome}</h5>
              </div>
              <div className="mb-2"><StatusBadge status={canal.status} /></div>
              <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                <div><strong>Numero:</strong> {formatNumero(canal.numero)}</div>
                <div><strong>Instance:</strong> <code>{canal.instance_name}</code></div>
                {canal.conectado_em && (
                  <div><strong>Conectado em:</strong> {new Date(canal.conectado_em).toLocaleString('pt-BR')}</div>
                )}
                {canal.ultimo_erro && (
                  <div className="text-danger"><strong>Erro:</strong> {canal.ultimo_erro}</div>
                )}
              </div>

              <div className="d-flex gap-2 mt-3 flex-wrap">
                {canal.status !== 'connected' && (
                  <button
                    className="btn btn-sm"
                    onClick={conectar}
                    disabled={salvando}
                    style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
                  >
                    <i className="bi bi-qr-code me-1" /> Conectar
                  </button>
                )}
                {canal.status === 'connected' && (
                  <button
                    className="btn btn-sm btn-outline-warning"
                    onClick={desconectar}
                    disabled={salvando}
                  >
                    <i className="bi bi-power me-1" /> Desconectar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {qrModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: 'var(--portal-surface)', color: 'var(--portal-text)' }}>
              <div className="modal-header" style={{ borderColor: 'var(--portal-border)' }}>
                <h5 className="modal-title">Parear o Martelinho</h5>
                <button className="btn-close btn-close-white" onClick={() => setQrModal(null)} />
              </div>
              <div className="modal-body text-center">
                {qrModal.base64 ? (
                  <>
                    <img
                      src={qrModal.base64.startsWith('data:') ? qrModal.base64 : `data:image/png;base64,${qrModal.base64}`}
                      alt="QR code"
                      style={{ width: 280, height: 280, background: '#fff', padding: 12, borderRadius: 12 }}
                    />
                    {qrModal.pairing && (
                      <p className="mt-3 mb-1" style={{ fontSize: '0.85rem', color: 'var(--portal-muted)' }}>
                        Ou use o codigo de pareamento:
                      </p>
                    )}
                    {qrModal.pairing && (
                      <code style={{ fontSize: '1.2rem', letterSpacing: 2 }}>{qrModal.pairing}</code>
                    )}
                    <p className="mt-3 mb-0" style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                      No celular: WhatsApp &gt; Aparelhos conectados &gt; Conectar um aparelho.
                      <br />O QR e atualizado a cada poucos segundos automaticamente.
                    </p>
                  </>
                ) : (
                  <div className="py-4">
                    <div className="spinner-border mb-3" />
                    <p className="text-muted mb-0">Gerando QR code...</p>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderColor: 'var(--portal-border)' }}>
                <button className="btn btn-outline-secondary" onClick={() => setQrModal(null)}>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
